/**
 * Work Progress Tracker Component
 * Shows real-time progress of planned works with status updates
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,

  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  CheckCircle as CompleteIcon,
  CheckCircle,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Assignment as TaskIcon,
  Group as TeamIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

import {
  PlannedWorkDetail,
  WorkTodoItem,
  getWorkStatusColor,
  getTodoStatusColor,
  WORK_STATUSES
} from '../../types/plannedWorks';
import plannedWorksService from '../../services/plannedWorksService';

interface WorkProgressTrackerProps {
  plannedWork: PlannedWorkDetail;
  onWorkUpdate?: (updatedWork: PlannedWorkDetail) => void;
  showActions?: boolean;
  compact?: boolean;
}

interface StatusUpdateDialogProps {
  open: boolean;
  currentStatus: string;
  onClose: () => void;
  onUpdate: (newStatus: string, notes: string) => void;
}

const StatusUpdateDialog: React.FC<StatusUpdateDialogProps> = ({
  open,
  currentStatus,
  onClose,
  onUpdate
}) => {
  const [newStatus, setNewStatus] = useState(currentStatus);
  const [notes, setNotes] = useState('');

  const availableTransitions = WORK_STATUSES.filter(status => {
    // Define valid status transitions
    const validTransitions: Record<string, string[]> = {
      'draft': ['proposed'],
      'proposed': ['under_review', 'cancelled'],
      'under_review': ['approved', 'draft'],
      'approved': ['scheduled', 'cancelled'],
      'scheduled': ['ready_to_execute', 'on_hold'],
      'ready_to_execute': ['in_progress', 'on_hold'],
      'in_progress': ['completed', 'on_hold'],
      'on_hold': ['ready_to_execute', 'cancelled'],
      'completed': [],
      'cancelled': []
    };

    return validTransitions[currentStatus]?.includes(status.value) || false;
  });

  const handleSubmit = () => {
    if (newStatus !== currentStatus) {
      onUpdate(newStatus, notes);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Work Status</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Current Status: 
              <Chip 
                label={WORK_STATUSES.find(s => s.value === currentStatus)?.label || currentStatus}
                color={getWorkStatusColor(currentStatus)}
                size="small"
                sx={{ ml: 1 }}
              />
            </Typography>
          </Box>

          <TextField
            select
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            SelectProps={{ native: true }}
            fullWidth
          >
            <option value={currentStatus}>Select new status...</option>
            {availableTransitions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </TextField>

          <TextField
            label="Status Update Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this status change..."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={newStatus === currentStatus}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const WorkProgressTracker: React.FC<WorkProgressTrackerProps> = ({
  plannedWork,
  onWorkUpdate,
  showActions = true,
  compact = false
}) => {
  const [loading, setLoading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate progress metrics
  const todoItems = plannedWork.todo_items || [];
  const progressStats = {
    total: todoItems.length,
    completed: todoItems.filter(item => item.status === 'completed').length,
    inProgress: todoItems.filter(item => item.status === 'in_progress').length,
    blocked: todoItems.filter(item => item.status === 'blocked').length,
    pending: todoItems.filter(item => item.status === 'pending').length
  };

  const overallProgress = progressStats.total > 0 
    ? (progressStats.completed / progressStats.total) * 100 
    : 0;

  // Time tracking
  const now = new Date();
  const startDate = plannedWork.actual_start_date 
    ? parseISO(plannedWork.actual_start_date)
    : plannedWork.proposed_start_date 
    ? parseISO(plannedWork.proposed_start_date)
    : null;

  const endDate = plannedWork.actual_end_date
    ? parseISO(plannedWork.actual_end_date)
    : plannedWork.proposed_end_date
    ? parseISO(plannedWork.proposed_end_date)
    : null;

  const isOverdue = endDate && now > endDate && plannedWork.status !== 'completed';
  const timeRemaining = endDate ? formatDistanceToNow(endDate, { addSuffix: true }) : null;

  const handleStatusUpdate = async (newStatus: string, notes: string) => {
    try {
      setLoading(true);
      setError(null);

      // Call appropriate API based on status transition
      let updatedWork: PlannedWorkDetail;

      switch (newStatus) {
        case 'in_progress':
          updatedWork = await plannedWorksService.plannedWorks.startExecution(plannedWork.id);
          break;
        case 'completed':
          updatedWork = await plannedWorksService.plannedWorks.complete(plannedWork.id, {
            completion_notes: notes,
            lessons_learned: ''
          });
          break;
        case 'scheduled':
          updatedWork = await plannedWorksService.plannedWorks.schedule(plannedWork.id, {});
          break;
        default:
          // Generic status update
          updatedWork = await plannedWorksService.plannedWorks.update(plannedWork.id, {
            // status: newStatus as any
          });
          break;
      }

      if (onWorkUpdate) {
        onWorkUpdate(updatedWork);
      }
    } catch (err) {
      console.error('Failed to update work status:', err);
      setError('Failed to update work status');
    } finally {
      setLoading(false);
    }
  };

  const renderProgressTimeline = () => {
    const statusHistory = [
      { status: 'draft', label: 'Created', completed: true },
      { status: 'proposed', label: 'Proposed', completed: ['proposed', 'under_review', 'approved', 'scheduled', 'in_progress', 'completed'].includes(plannedWork.status) },
      { status: 'approved', label: 'Approved', completed: ['approved', 'scheduled', 'in_progress', 'completed'].includes(plannedWork.status) },
      { status: 'in_progress', label: 'In Progress', completed: ['in_progress', 'completed'].includes(plannedWork.status) },
      { status: 'completed', label: 'Completed', completed: plannedWork.status === 'completed' }
    ];

    return (
      <Timeline sx={{ p: 0, m: 0 }}>
        {statusHistory.map((item, index) => (
          <TimelineItem key={item.status}>
            <TimelineSeparator>
              <TimelineDot color={item.completed ? 'success' : 'grey'}>
                {item.completed && <CheckCircle fontSize="small" />}
              </TimelineDot>
              {index < statusHistory.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Typography 
                variant="body2" 
                color={item.completed ? 'text.primary' : 'text.secondary'}
              >
                {item.label}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    );
  };

  const renderTaskProgress = () => {
    if (todoItems.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No tasks defined for this work yet.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Task Progress ({progressStats.completed}/{progressStats.total})
        </Typography>
        
        <LinearProgress 
          variant="determinate" 
          value={overallProgress} 
          sx={{ mb: 2, height: 8, borderRadius: 4 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {progressStats.completed}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Completed
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary.main">
                {progressStats.inProgress}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                In Progress
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">
                {progressStats.blocked}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Blocked
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                {progressStats.pending}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Pending
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {!compact && progressStats.blocked > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {progressStats.blocked} task(s) are blocked and require attention.
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Work Progress
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={plannedWork.status_display}
                color={getWorkStatusColor(plannedWork.status)}
              />
              {isOverdue && (
                <Chip
                  label="Overdue"
                  color="error"
                  icon={<WarningIcon />}
                  size="small"
                />
              )}
            </Box>
          </Box>

          {showActions && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setStatusDialogOpen(true)}
              disabled={loading || plannedWork.status === 'completed'}
            >
              Update Status
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Timeline */}
          {!compact && (
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Status Timeline
              </Typography>
              {renderProgressTimeline()}
            </Grid>
          )}

          {/* Work Details */}
          <Grid item xs={12} md={compact ? 12 : 4}>
            <Typography variant="subtitle2" gutterBottom>
              Work Details
            </Typography>
            
            <List dense>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Assigned To"
                  secondary={plannedWork.assigned_to_name || 'Unassigned'}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <ScheduleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Schedule"
                  secondary={
                    startDate && endDate
                      ? `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd, yyyy')}`
                      : 'Not scheduled'
                  }
                />
              </ListItem>

              {timeRemaining && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <TimerIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Time Remaining"
                    secondary={timeRemaining}
                  />
                </ListItem>
              )}

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <TaskIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Tasks"
                  secondary={`${progressStats.completed}/${progressStats.total} completed`}
                />
              </ListItem>
            </List>
          </Grid>

          {/* Task Progress */}
          <Grid item xs={12} md={compact ? 12 : 4}>
            {renderTaskProgress()}
          </Grid>
        </Grid>

        {/* Recent Activity */}
        {!compact && todoItems.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Recent Activity
            </Typography>
            
            <List dense>
              {todoItems
                .filter(item => item.completed_at)
                .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())
                .slice(0, 3)
                .map((item) => (
                  <ListItem key={item.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>
                        <CheckCircle fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={`Completed ${formatDistanceToNow(new Date(item.completed_at!), { addSuffix: true })} by ${item.completed_by_name}`}
                    />
                  </ListItem>
                ))}
            </List>
          </Box>
        )}
      </CardContent>

      {/* Status Update Dialog */}
      <StatusUpdateDialog
        open={statusDialogOpen}
        currentStatus={plannedWork.status}
        onClose={() => setStatusDialogOpen(false)}
        onUpdate={handleStatusUpdate}
      />
    </Card>
  );
};
